import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Category display order and groupings
const CATEGORY_GROUPS = [
  {
    title: 'Print',
    categories: ['Advertising', 'Stationery & Cards', 'Packaging', 'Office Supplies', 'Restaurants & Hotels', 'Book Printing'],
  },
  {
    title: 'Signage & Displays',
    categories: ['Flexible Materials', 'Adhesive Advertising', 'Advertising Panels', 'Outdoor Displays'],
  },
];

const BADGE_STYLES = {
  new: { text: 'NEW', className: 'bg-coral/10 text-coral border-coral/30' },
  best_seller: { text: 'BEST SELLER', className: 'bg-mint/10 text-mint-dark border-mint/30' },
  '2026': { text: '2026', className: 'bg-navy/10 text-navy border-navy/20' },
  sheets_rolls: { text: 'SHEETS & ROLLS', className: 'bg-coral/10 text-coral border-coral/30' },
};

function Badge({ type }) {
  const style = BADGE_STYLES[type];
  if (!style) return null;
  return (
    <span className={`inline-flex text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded border ${style.className}`}>
      {style.text}
    </span>
  );
}

export function ProductsPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await api.get('/api/products');
        setProducts(res.data);
      } catch {
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Group products by category
  const grouped = {};
  products.forEach((p) => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  // Filter by search
  const matchesSearch = (p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
  };

  // If a category is selected, show product cards for that category
  if (selectedCategory) {
    const categoryProducts = products.filter(
      (p) => p.category === selectedCategory && matchesSearch(p)
    );

    return (
      <div className="min-h-screen bg-white py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-mint hover:text-mint-dark font-medium text-sm"
              data-testid="back-to-catalog"
            >
              All Products
            </button>
            <span className="text-gray-300">/</span>
            <h1 className="text-2xl font-heading font-bold text-navy">{selectedCategory}</h1>
            <span className="text-gray-400 text-sm">({categoryProducts.length})</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categoryProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Link
                  to={`/products/${product.id}`}
                  className="block group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden card-hover">
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-navy group-hover:text-mint transition-colors">
                            {product.name}
                          </h3>
                          {product.badge && (
                            <div className="mt-1.5">
                              <Badge type={product.badge} />
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{product.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-semibold text-navy">
                          From ${product.base_price?.toFixed(2)}
                        </span>
                        <span className="text-sm text-mint font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          View <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default: catalog overview layout (matching the screenshot design)
  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">
              Our Products
            </h1>
            <p className="text-gray-500 mt-1">{products.length} products across {Object.keys(grouped).length} categories</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="product-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : (
          <>
            {CATEGORY_GROUPS.map((group, gi) => (
              <div key={group.title} className="mb-12">
                {/* Group Divider */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px flex-1 bg-gray-200" />
                  <h2 className="text-xs uppercase tracking-[0.3em] text-gray-400 font-semibold">{group.title}</h2>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                {/* Category Columns */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-8 gap-y-10">
                  {group.categories.map((catName) => {
                    const catProducts = (grouped[catName] || []).filter(matchesSearch);
                    if (catProducts.length === 0 && searchQuery) return null;

                    return (
                      <motion.div
                        key={catName}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: gi * 0.1 }}
                      >
                        {/* Category Header */}
                        <button
                          onClick={() => setSelectedCategory(catName)}
                          className="text-left w-full group mb-3"
                          data-testid={`cat-${catName.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <h3 className="font-heading font-bold text-navy text-base group-hover:text-mint transition-colors">
                            {catName}
                          </h3>
                        </button>

                        {/* Product List */}
                        <ul className="space-y-2">
                          {catProducts.map((product) => (
                            <li key={product.id}>
                              <Link
                                to={`/products/${product.id}`}
                                className="text-sm text-gray-600 hover:text-navy transition-colors flex items-center gap-2 leading-snug"
                                data-testid={`product-link-${product.id}`}
                              >
                                <span>{product.name}</span>
                                {product.badge && <Badge type={product.badge} />}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
